import { action, makeObservable, observable, runInAction } from 'mobx';
import { Common } from 'src/AppState/Common';
import { SdkStaff } from 'src/AppState/sdk/staff';
import { ConfigState } from 'src/AppState/ConfigState/ConfigState';
import { apiSetSelfExclusionManualRequest } from 'src/api_openapi/v2/compliance/apiSetSelfExclusionManual';
import {
    apiSetRequestReactivationRequest,
    ApiSetRequestReactivationResponseType,
} from 'src/api_openapi/v2/compliance/apiSetRequestReactivation';
import {
    apiDeleteReactivationRequest,
    ApiDeleteReactivationResponseType,
} from 'src/api_openapi/v2/compliance/apiDeleteReactivation';
import {
    apiGetSelfExclusionStatusRequest,
    ApiGetSelfExclusionStatusResponseType,
} from 'src/api_openapi/v2/compliance/apiGetSelfExclusionStatus';
import {
    ComplianceExclusionInfoFullType,
    ComplianceCustomerStatusExclusionInfoFullType,
    ComplianceCustomerIdentifierType,
} from 'src/api_openapi/v2/compliance/schemas';

export class ReactivateSelfExclusionStore {
    private readonly configState: ConfigState;

    @observable public exclusionStatus: ComplianceExclusionInfoFullType | null | undefined = undefined;
    @observable public onMatchData: ComplianceCustomerStatusExclusionInfoFullType[] | null | undefined = undefined;
    @observable public onMatchAccountData: Map<number, { firstName: string; surname: string; status: string }> =
        new Map();

    public constructor(
        private readonly common: Common,
        private readonly customerId: number,
        private readonly sdkStaff: SdkStaff
    ) {
        this.configState = ConfigState.get(this.common);
        makeObservable(this);
    }

    private async fetchExclusionStatuses(
        accounts: ComplianceCustomerIdentifierType[]
    ): Promise<ApiGetSelfExclusionStatusResponseType> {
        const { gatewayUrl } = this.common.appEnvParams;
        return apiGetSelfExclusionStatusRequest(
            gatewayUrl,
            { requestBody: accounts },
            { authorization: `Bearer ${this.common.session.currentJwt}` }
        );
    }

    @action public async fetchSelfExclusionStatus(): Promise<void> {
        const response = await this.fetchExclusionStatuses([
            {
                accountId: this.customerId,
                universe: this.configState.universe,
            },
        ]);

        if (response.status === 200) {
            runInAction(() => {
                const first = response.body[0];
                this.exclusionStatus = first?.status ?? null;
            });
        }
    }

    @action public async fetchOnMatchExclusions(): Promise<void> {
        const matchesResponse = await this.common.trpcClient.client.accountData.getSelfExclusionMatches.query({
            accountId: this.customerId,
        });

        if (matchesResponse.responseStatus !== 'success') {
            runInAction(() => {
                this.onMatchData = null;
            });
            return;
        }

        const matchedAccounts = matchesResponse.data;

        if (matchedAccounts.length === 0) {
            runInAction(() => {
                this.onMatchData = [];
            });
            return;
        }

        const statusResponse = await this.fetchExclusionStatuses(matchedAccounts);

        if (statusResponse.status === 200) {
            const accountIds = matchedAccounts.map((a) => a.accountId);
            const accountDataResponse = await this.common.trpcClient.client.accountData.searchAccountListByIds.mutate({
                ids: accountIds,
                include: ['id', 'properties.firstName', 'properties.surname', 'properties.status'],
            });

            runInAction(() => {
                this.onMatchData = statusResponse.body;

                if (accountDataResponse.responseStatus === 'success') {
                    for (const result of accountDataResponse.data.results) {
                        this.onMatchAccountData.set(result.id, {
                            firstName: result.properties.firstName ?? '',
                            surname: result.properties.surname ?? '',
                            status: result.properties.status ?? '',
                        });
                    }
                }
            });
        } else {
            runInAction(() => {
                this.onMatchData = null;
            });
        }
    }

    public async setSelfExclusion(months: number): Promise<void> {
        const { complianceNew } = this.configState.config;

        if (complianceNew) {
            const { gatewayUrl } = this.common.appEnvParams;
            const response = await apiSetSelfExclusionManualRequest(
                gatewayUrl,
                {
                    requestBody: {
                        durationMonths: months,
                        accountId: this.customerId,
                    },
                },
                { authorization: `Bearer ${this.common.session.currentJwt}` }
            );

            if (response.status !== 200) {
                throw new Error(`Unexpected response status: ${response.status}`);
            }

            runInAction(() => {
                this.exclusionStatus = response.body.customer.status ?? null;
            });
        } else {
            await this.sdkStaff.setSelfExclusion({
                customer_id: this.customerId,
                requestBody: {
                    months,
                },
            });
        }
    }

    private async handleReactivationResponse(
        complianceRequest: () => Promise<ApiSetRequestReactivationResponseType | ApiDeleteReactivationResponseType>,
        legacyRequest: () => Promise<unknown>
    ): Promise<void> {
        const { complianceNew } = this.configState.config;

        if (complianceNew) {
            const response = await complianceRequest();

            if (response.status === 400) {
                throw new Error(response.body);
            }

            if (response.status !== 200) {
                throw new Error(`Unexpected response status: ${response.status}`);
            }

            runInAction(() => {
                this.exclusionStatus = response.body.status ?? null;
            });
        } else {
            await legacyRequest();
        }
    }

    public async requestReactivation(): Promise<void> {
        const { gatewayUrl } = this.common.appEnvParams;
        const auth = { authorization: `Bearer ${this.common.session.currentJwt}` };

        await this.handleReactivationResponse(
            () => apiSetRequestReactivationRequest(gatewayUrl, { account_id: this.customerId }, auth),
            () => this.sdkStaff.requestReactivationSelfExclusion({ customer_id: this.customerId, requestBody: {} })
        );
    }

    public async reactivate(): Promise<void> {
        const { gatewayUrl } = this.common.appEnvParams;
        const auth = { authorization: `Bearer ${this.common.session.currentJwt}` };

        await this.handleReactivationResponse(
            () => apiDeleteReactivationRequest(gatewayUrl, { account_id: this.customerId }, auth),
            () => this.sdkStaff.reactivateSelfExclusion({ customer_id: this.customerId, requestBody: {} })
        );
    }
}
